"use client";

import { useMarketingAnalytics } from "@analytics";
import { MarketingLandingVideoFullscreenControls } from "@marketing/shared/components/marketing-landing-video-nav-chips";
import { useMarketingLandingVideoOverlay } from "@marketing/shared/components/marketing-landing-video-overlay-context";
import { PortraitRotateHintLayer } from "@marketing/shared/components/portrait-rotate-hint-layer";
import { usePortraitRotateHint } from "@marketing/shared/hooks/use-portrait-rotate-hint";
import {
	MARKETING_LANDING_VIDEO_DIALOG_CHROME_FULLSCREEN_Z_CLASS,
	marketingLandingVideoOverlayTopStyle,
	resolveMarketingLandingVideoDialogSurfaceStyle,
} from "@marketing/shared/lib/marketing-landing-video-chrome-layout";
import {
	exitMarketingVideoFullscreen,
	isMarketingVideoFullscreenActive,
} from "@marketing/shared/lib/marketing-landing-video-fullscreen";
import { preventDismissWhenMarketingSiteHeaderTarget } from "@marketing/shared/lib/marketing-site-header-outside-interaction";
import { safeExitDocumentFullscreen } from "@marketing/shared/lib/safe-document-fullscreen";
import { safeMatchMediaMatches } from "@marketing/shared/lib/safe-match-media";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@repo/ui";
import type { ComponentType } from "react";
import * as React from "react";
import { useMediaQuery } from "usehooks-ts";
import {
	loadLandingVideoPlayer,
	resolveLandingVideoKind,
} from "../lib/landing-video/resolve-landing-video-player";

export interface LandingVideoDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/**
	 * Media URL for react-player v3 (`src`): site-relative path, full URL, or YouTube/Vimeo.
	 * Site-relative paths are resolved to absolute URLs for reliable loading.
	 */
	videoUrl: string;
	/**
	 * When false, skip the mobile portrait “rotate to landscape” overlay and do not gate playback for it.
	 * @default true
	 */
	portraitRotateHintEnabled?: boolean;
	/** How long the portrait hint stays visible before fading (ms). @default 2000 */
	portraitRotateHintMaxMs?: number;
}

const FADE_MS = 200;

/** Absolute URL for site-relative paths so `src` always hits static files, not locale middleware. */
function resolvedPlayerSrc(url: string): string {
	if (typeof window === "undefined") {
		return url;
	}
	if (url.startsWith("/")) {
		return `${window.location.origin}${url}`;
	}
	return url;
}

export function LandingVideoDialog({
	open,
	onOpenChange,
	videoUrl,
	portraitRotateHintEnabled = true,
	portraitRotateHintMaxMs,
}: LandingVideoDialogProps) {
	const {
		muted,
		setMuted,
		isFullscreen,
		setChromeFullscreen,
		fullscreenTargetRef,
		registerRequestClose,
	} = useMarketingLandingVideoOverlay();

	const [isClosing, setIsClosing] = React.useState(false);
	const [fadeInReady, setFadeInReady] = React.useState(false);
	const userPausedRef = React.useRef(false);
	const suppressNextPauseRef = React.useRef(false);
	const nativeVideoRef = React.useRef<HTMLVideoElement | null>(null);
	type PlayerComponent = ComponentType<{
		src?: string;
		playing?: boolean;
		autoPlay?: boolean;
		muted?: boolean;
		controls?: boolean;
		playsInline?: boolean;
		preload?: string;
		width?: string | number;
		height?: string | number;
		onEnded?: () => void;
		onError?: (event: unknown) => void;
		/** Native controls change `muted` without updating overlay context; sync navbar + chrome. */
		onVolumeChange?: React.ReactEventHandler<HTMLVideoElement>;
		onTimeUpdate?: React.ReactEventHandler<HTMLVideoElement>;
		onPlay?: React.ReactEventHandler<HTMLVideoElement>;
		onPause?: React.ReactEventHandler<HTMLVideoElement>;
	}>;

	const [Player, setPlayer] = React.useState<PlayerComponent | null>(null);

	const isCoarsePointer = useMediaQuery("(pointer: coarse)");
	const isSmallViewport = useMediaQuery("(max-width: 640px)");
	const isMobile = isCoarsePointer || isSmallViewport;

	const portraitHint = usePortraitRotateHint({
		active: open,
		enabled: portraitRotateHintEnabled,
		maxDisplayMs: portraitRotateHintMaxMs,
		isClosing,
	});
	const rotateOverlayVisible = portraitHint.overlayVisible;
	const canStartPlayback = portraitHint.primaryExperienceAllowed;

	/** Fresh values for fullscreen/orientation helpers. */
	const openRef = React.useRef(open);
	const isClosingRef = React.useRef(isClosing);
	const isFullscreenRef = React.useRef(isFullscreen);
	const escapeHandledRef = React.useRef(false);

	openRef.current = open;
	isClosingRef.current = isClosing;
	isFullscreenRef.current = isFullscreen;

	const playerSrc = resolvedPlayerSrc(videoUrl);
	const videoKind = resolveLandingVideoKind(videoUrl);
	const useNativeVideo = videoKind === "file";

	const syncMutedFromPlayer: React.ReactEventHandler<HTMLVideoElement> =
		React.useCallback(
			(event) => {
				setMuted(event.currentTarget.muted);
			},
			[setMuted],
		);

	const requestClose = React.useCallback(() => {
		void exitMarketingVideoFullscreen(fullscreenTargetRef.current);
		void safeExitDocumentFullscreen();
		setIsClosing(true);
		setFadeInReady(false);
		window.setTimeout(() => {
			onOpenChange(false);
		}, FADE_MS);
	}, [fullscreenTargetRef, onOpenChange]);

	const {
		landingVideoPlayStarted,
		landingVideoProgress,
		landingVideoMuted,
		landingVideoUnmuted,
		landingVideoFullscreenEntered,
		landingVideoFullscreenExited,
	} = useMarketingAnalytics();

	const milestoneHitRef = React.useRef({
		25: false,
		50: false,
		75: false,
		100: false,
	});
	const playStartedRef = React.useRef(false);
	const prevMutedForAnalyticsRef = React.useRef<boolean | null>(null);
	const prevFullscreenForAnalyticsRef = React.useRef<boolean | null>(null);
	const playbackAllowedRef = React.useRef(false);

	React.useEffect(() => {
		if (open) {
			milestoneHitRef.current = {
				25: false,
				50: false,
				75: false,
				100: false,
			};
			playStartedRef.current = false;
			prevMutedForAnalyticsRef.current = null;
			prevFullscreenForAnalyticsRef.current = null;
		}
	}, [open]);

	const maybeReportVideoProgress = React.useCallback(
		(video: HTMLVideoElement) => {
			const duration = video.duration;
			if (!Number.isFinite(duration) || duration <= 0) {
				return;
			}
			const pct = (video.currentTime / duration) * 100;
			const mark = (milestone: 25 | 50 | 75 | 100) => {
				if (milestoneHitRef.current[milestone]) {
					return;
				}
				if (pct + 0.25 < milestone) {
					return;
				}
				milestoneHitRef.current[milestone] = true;
				landingVideoProgress({
					milestone,
					video_kind: videoKind,
					src: videoUrl,
				});
			};
			if (pct >= 99.5) {
				mark(100);
				return;
			}
			if (pct >= 75) {
				mark(75);
			}
			if (pct >= 50) {
				mark(50);
			}
			if (pct >= 25) {
				mark(25);
			}
		},
		[landingVideoProgress, videoKind, videoUrl],
	);

	const handleVideoTimeUpdate = React.useCallback<
		React.ReactEventHandler<HTMLVideoElement>
	>(
		(event) => {
			if (!playbackAllowedRef.current) {
				return;
			}
			maybeReportVideoProgress(event.currentTarget);
		},
		[maybeReportVideoProgress],
	);

	const handleVideoPlay = React.useCallback<
		React.ReactEventHandler<HTMLVideoElement>
	>(
		(_event) => {
			userPausedRef.current = false;
			if (!playbackAllowedRef.current) {
				return;
			}
			if (!playStartedRef.current) {
				playStartedRef.current = true;
				landingVideoPlayStarted({
					video_kind: videoKind,
					src: videoUrl,
				});
			}
		},
		[landingVideoPlayStarted, videoKind, videoUrl],
	);

	const handleVideoEnded = React.useCallback(() => {
		if (!milestoneHitRef.current[100]) {
			milestoneHitRef.current[100] = true;
			landingVideoProgress({
				milestone: 100,
				video_kind: videoKind,
				src: videoUrl,
			});
		}
		requestClose();
	}, [landingVideoProgress, requestClose, videoKind, videoUrl]);

	const handleDialogOpenChange = React.useCallback(
		(nextOpen: boolean) => {
			if (nextOpen) {
				onOpenChange(true);
			}
		},
		[onOpenChange],
	);

	const handleEscapeKey = React.useCallback(
		(event: KeyboardEvent | React.KeyboardEvent) => {
			try {
				if (event.key !== "Escape") {
					return;
				}

				if (escapeHandledRef.current) {
					event.preventDefault();
					event.stopPropagation();
					return;
				}
				escapeHandledRef.current = true;
				window.queueMicrotask(() => {
					escapeHandledRef.current = false;
				});

				event.preventDefault();
				event.stopPropagation();

				if (isClosingRef.current) {
					return;
				}

				const shell = fullscreenTargetRef.current;
				if (shell != null && isMarketingVideoFullscreenActive(shell)) {
					return;
				}

				if (isFullscreenRef.current) {
					isFullscreenRef.current = false;
					setChromeFullscreen(false);
					return;
				}

				requestClose();
			} catch {
				/* Best-effort keyboard handling; never take down the page. */
			}
		},
		[fullscreenTargetRef, requestClose, setChromeFullscreen],
	);

	React.useEffect(() => {
		if (!open || isClosing || !fadeInReady) {
			return;
		}

		const onKeyDown = (event: KeyboardEvent) => {
			handleEscapeKey(event);
		};

		window.addEventListener("keydown", onKeyDown, true);
		return () => {
			window.removeEventListener("keydown", onKeyDown, true);
		};
	}, [fadeInReady, handleEscapeKey, isClosing, open]);

	React.useEffect(() => {
		return registerRequestClose(requestClose);
	}, [registerRequestClose, requestClose]);

	React.useEffect(() => {
		if (open) {
			setIsClosing(false);
			setFadeInReady(false);
			userPausedRef.current = false;
			return;
		}
		setIsClosing(false);
		setFadeInReady(false);
		userPausedRef.current = false;
		setPlayer(null);
	}, [open]);

	React.useEffect(() => {
		if (!open) {
			return;
		}
		const id = window.requestAnimationFrame(() => {
			setFadeInReady(true);
		});
		return () => window.cancelAnimationFrame(id);
	}, [open]);

	React.useEffect(() => {
		if (!open) {
			return;
		}

		if (useNativeVideo) {
			setPlayer(null);
			return;
		}

		let cancelled = false;
		loadLandingVideoPlayer(videoUrl)
			.then((Comp) => {
				if (!cancelled) {
					setPlayer(() => Comp as unknown as PlayerComponent);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setPlayer(null);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [open, useNativeVideo, videoUrl]);

	/**
	 * Mobile: chrome fullscreen in landscape (opening already landscape or rotating into it).
	 * Portrait: exit chrome fullscreen so the dialog shell + rotate hint work.
	 */
	React.useEffect(() => {
		if (!open || !isMobile) {
			return;
		}

		const exitIfPortraitFullscreen = () => {
			if (!safeMatchMediaMatches("(orientation: portrait)")) {
				return;
			}
			setChromeFullscreen(false);
		};

		const tryEnterLandscapeFullscreen = () => {
			if (!safeMatchMediaMatches("(orientation: landscape)")) {
				return;
			}
			if (!openRef.current || isClosingRef.current) {
				return;
			}
			if (!fadeInReady) {
				return;
			}
			// Do not gate on `canStartPlayback`: portrait hint can leave that false for a tick while
			// `orientation: landscape` is already true; chrome fullscreen should still engage.
			setChromeFullscreen(true);
		};

		const landscapeFullscreenTimeoutIds: number[] = [];

		const scheduleLandscapeFullscreen = () => {
			for (const delayMs of [0, 120, 320]) {
				landscapeFullscreenTimeoutIds.push(
					window.setTimeout(() => {
						tryEnterLandscapeFullscreen();
					}, delayMs),
				);
			}
		};

		const onLayout = () => {
			exitIfPortraitFullscreen();
			scheduleLandscapeFullscreen();
		};

		window.addEventListener("orientationchange", onLayout);
		window.addEventListener("resize", onLayout);
		onLayout();

		return () => {
			window.removeEventListener("orientationchange", onLayout);
			window.removeEventListener("resize", onLayout);
			for (const timeoutId of landscapeFullscreenTimeoutIds) {
				window.clearTimeout(timeoutId);
			}
		};
	}, [open, isMobile, fadeInReady, setChromeFullscreen]);

	const isVisible = open && !isClosing && fadeInReady;
	const playbackAllowed = open && !isClosing && canStartPlayback;
	playbackAllowedRef.current = playbackAllowed;

	React.useEffect(() => {
		if (!open) {
			return;
		}
		if (prevMutedForAnalyticsRef.current === null) {
			prevMutedForAnalyticsRef.current = muted;
			return;
		}
		if (prevMutedForAnalyticsRef.current === muted) {
			return;
		}
		if (muted) {
			landingVideoMuted();
		} else {
			landingVideoUnmuted();
		}
		prevMutedForAnalyticsRef.current = muted;
	}, [landingVideoMuted, landingVideoUnmuted, muted, open]);

	React.useEffect(() => {
		if (!open) {
			return;
		}
		if (prevFullscreenForAnalyticsRef.current === null) {
			prevFullscreenForAnalyticsRef.current = isFullscreen;
			return;
		}
		if (prevFullscreenForAnalyticsRef.current === isFullscreen) {
			return;
		}
		if (isFullscreen) {
			landingVideoFullscreenEntered();
		} else {
			landingVideoFullscreenExited();
		}
		prevFullscreenForAnalyticsRef.current = isFullscreen;
	}, [
		isFullscreen,
		landingVideoFullscreenEntered,
		landingVideoFullscreenExited,
		open,
	]);
	/**
	 * Best-effort: if we temporarily disallow playback (rotate overlay / closing), pause once.
	 * We do NOT drive play/pause from React state on every click — keep the media surface stable.
	 */
	React.useEffect(() => {
		if (!open) {
			return;
		}
		const video =
			nativeVideoRef.current ??
			fullscreenTargetRef.current?.querySelector("video");
		if (!(video instanceof HTMLVideoElement)) {
			return;
		}

		if (!playbackAllowed) {
			suppressNextPauseRef.current = true;
			try {
				video.pause();
			} catch {
				/* ignore */
			}
			return;
		}

		// Gate opened: resume unless user explicitly paused.
		if (!userPausedRef.current) {
			void video.play().catch(() => {});
		}
	}, [open, playbackAllowed, fullscreenTargetRef]);
	const marketingOverlayTop = marketingLandingVideoOverlayTopStyle();
	const dialogSurfaceStyle =
		resolveMarketingLandingVideoDialogSurfaceStyle(isFullscreen);

	return (
		<DialogPrimitive.Root
			modal={false}
			open={open}
			onOpenChange={handleDialogOpenChange}
		>			{open ? (
				<DialogPrimitive.Portal>
					<DialogPrimitive.Overlay
						className={cn(
							"fixed inset-x-0 bottom-0 z-[160] bg-black/80 backdrop-blur-xs",
							"transition-opacity duration-200 ease-out motion-reduce:transition-none",
							isVisible ? "opacity-100" : "opacity-0",
						)}
						style={marketingOverlayTop}
					/>
					<DialogPrimitive.Content
						aria-describedby={undefined}
						onOpenAutoFocus={(e) => e.preventDefault()}
						onEscapeKeyDown={handleEscapeKey}
						onPointerDownOutside={(e) => {
							preventDismissWhenMarketingSiteHeaderTarget(
								e,
								e.target,
							);
						}}
						onFocusOutside={(e) => {
							preventDismissWhenMarketingSiteHeaderTarget(
								e,
								e.target,
							);
						}}
						onInteractOutside={(e) => {
							preventDismissWhenMarketingSiteHeaderTarget(
								e,
								e.target,
							);
						}}
						className={cn(
							"fixed inset-x-0 bottom-0 z-[161] flex max-h-none flex-col overflow-hidden border-0 bg-background p-0 shadow-xl outline-none",
							"transition-opacity duration-200 ease-out motion-reduce:transition-none",
							isVisible ? "opacity-100" : "opacity-0",
							isFullscreen &&
								MARKETING_LANDING_VIDEO_DIALOG_CHROME_FULLSCREEN_Z_CLASS,
						)}
						style={dialogSurfaceStyle}
					>
						<DialogPrimitive.Title className="sr-only">
							Landing video
						</DialogPrimitive.Title>

						<div
							ref={fullscreenTargetRef}
							className="relative flex h-full min-h-0 flex-1 flex-col bg-black"
						>
							{isFullscreen ? (
								<MarketingLandingVideoFullscreenControls />
							) : null}

							<div className="relative flex min-h-0 flex-1 items-center justify-center">
								{useNativeVideo ? (
									<video
										key={playerSrc}
										ref={nativeVideoRef}
										src={playerSrc}
										className="h-full w-full bg-black"
										style={{ objectFit: "contain" }}
										preload="auto"
										controls
										playsInline
										muted={muted}
										autoPlay={false}
										onEnded={handleVideoEnded}
										onPlay={handleVideoPlay}
										onTimeUpdate={handleVideoTimeUpdate}
										onPause={(_e) => {
											if (suppressNextPauseRef.current) {
												suppressNextPauseRef.current = false;
												return;
											}
											// If the browser pauses because playback is disallowed, treat as suppressed.
											if (!playbackAllowed) {
												return;
											}
											userPausedRef.current = true;
										}}
										onVolumeChange={(e) => {
											syncMutedFromPlayer(e);
										}}
									/>
								) : Player != null ? (
									<Player
										key={playerSrc}
										src={playerSrc}
										autoPlay={false}
										muted={muted}
										controls
										playsInline
										preload="auto"
										width="100%"
										height="100%"
										onEnded={handleVideoEnded}
										onError={() => {
											/* react-player / media element errors */
										}}
										onPlay={handleVideoPlay}
										onTimeUpdate={handleVideoTimeUpdate}
										onPause={() => {
											if (suppressNextPauseRef.current) {
												suppressNextPauseRef.current = false;
												return;
											}
											if (!playbackAllowed) {
												return;
											}
											userPausedRef.current = true;
										}}
										onVolumeChange={syncMutedFromPlayer}
									/>
								) : (
									<div className="px-6 text-center text-white/70">
										<p className="font-semibold">
											Loading video…
										</p>
									</div>
								)}
							</div>

							{portraitRotateHintEnabled ? (
								<PortraitRotateHintLayer
									position="absolute"
									visible={rotateOverlayVisible}
								/>
							) : null}
						</div>
					</DialogPrimitive.Content>
				</DialogPrimitive.Portal>
			) : null}
		</DialogPrimitive.Root>
	);
}
